import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { IAppEnv } from '../appEnvironment';
import { observable } from 'mobx';
import { TopOfferType } from '../api/topOfferType';
import { eventListWithDetailModel } from './eventListWithDetailModel';
import { EventDataStore } from './event';
import { IGroupedApiCtx, ProgressStateModelForStore, IWebTopBetCategory, protoUtils, ISBApiOperationResponse, IWebTopBetsWithTs, SportsBettingApiResult, errorIsCancel, ProgressStateModel, StatisticsInApiStore, StatisticsType, OddsPopularityStatsInApiStore } from 'eTip--common';
import { ISportsBettingApi } from '../api/api';

export const categoryStoreKey = 'categoryStore';
export const CategoryStore = types.model(categoryStoreKey, {
    id: types.string,
    name: types.string,
    sequence: types.number
});

export const topOfferStoreKey = 'topOfferStore';
export const TopOfferStore = types
    .model(topOfferStoreKey, {
        defaultCategoryId: types.maybeNull(types.string),
        categories: types.optional(types.array(CategoryStore), []),
        categoryContents: types.optional(types.map(eventListWithDetailModel), {}),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        lastReloadTime: types.maybeNull(types.number)
    })
    .views(self => {
        const env = getEnv<IAppEnv>(self);
        return {
            get refreshTime() {
                return env.settings.topBetsRefreshTime;
            }
        }
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);

        let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
        const loadAsync: (params: { categoryId?: string; eventId?: number; detailId?: number }, withDefaults: boolean) => Promise<void> = flow(function* (params: { categoryId?: string; eventId?: number; detailId?: number }, withDefaults: boolean) {
            self.state = ProgressStateModel.Loading;
            try {
                if (ctx && ctx.isOpen()) {
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();

                const r: ISBApiOperationResponse<IWebTopBetsWithTs> = yield ctx.api.getWebTopBetsAsync(TopOfferType.TopPonuka, null, self.lastReloadTime);
                if (r.Result === SportsBettingApiResult.Success) {
                    setEvents(r.ReturnValue.TopBets.Categories, withDefaults);
                    self.lastReloadTime = r.ReturnValue.Timestamp;
                    if (params.categoryId) {
                        self.defaultCategoryId = params.categoryId;
                    }

                    const activeContent = self.categoryContents.get(self.defaultCategoryId);
                    if (activeContent) {
                        if (params.eventId) {
                            activeContent.showDetailAsync({ eventKey: `${params.eventId}c${params.categoryId}`, isLive: false }, false);
                        }
                        else if (!!activeContent.defaults && !!activeContent.defaults.eventId) {
                            activeContent.showDetailAsync({ eventKey: `${activeContent.defaults.eventId}c${activeContent.defaults.categoryId}`, isLive: false }, false);
                        }
                        else activeContent.hideDetail();
                    }
                }
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in top bets list loading.', error);
                self.state = ProgressStateModel.Failed;
            } finally {
                ctx = null;
            }
        });

        const reloadAsync: (params: { categoryId?: string; eventId?: number; detailId?: number }, withDefaults: boolean) => Promise<void> = flow(function* (params: { categoryId?: string; eventId?: number; detailId?: number }, withDefaults: boolean) {
            try {
                if (ctx && ctx.isOpen()) {  //TODO incorrect, reload must not cancel full load (see e.g. topBetsBase)
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                const r: ISBApiOperationResponse<IWebTopBetsWithTs> = yield ctx.api.getWebTopBetsAsync(TopOfferType.TopPonuka, null, self.lastReloadTime);
                if (r.Result === SportsBettingApiResult.Success) {
                    setEvents(r.ReturnValue.TopBets.Categories, withDefaults);
                    self.lastReloadTime = r.ReturnValue.Timestamp;

                    const activeContent = self.categoryContents.get(params.categoryId);
                    if (activeContent) {
                        if (params.eventId) {
                            activeContent.showDetailAsync({ eventKey: `${params.eventId}c${params.categoryId}`, isLive: false }, false);
                        }
                        else if (activeContent.defaults && activeContent.defaults.eventId) {
                            activeContent.showDetailAsync({ eventKey: `${activeContent.defaults.eventId}c${activeContent.defaults.categoryId}`, isLive: false }, false);
                        }
                        else {
                            activeContent.hideDetail();
                        }
                    }
                } else if (self.state === ProgressStateModel.Success) {
                    let activeContent = self.categoryContents.get(params.categoryId);
                    if (activeContent && activeContent.detail) {
                        activeContent.reloadDetailAsync(true); //not waiting for finish, ignoring some errors
                    }
                }
                //reload must not change state  self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in top bets list loading.', error);
            } finally {
                ctx = null;
            }
        });

        const setEvents = (items: IWebTopBetCategory[], withDefaults: boolean) => {
            const categories: Instance<typeof CategoryStore>[] = [];

            //item.Sequence, comes 0, now using order in source array
            items/*.sort(createComparerOfSorting(i => i.Sequence, SortType.Desc))*/.forEach((item, itemIdx) => {
                if (itemIdx === 0) {
                    self.defaultCategoryId = item.Base.Id;
                }
                categories.push({
                    id: item.Base.Id,
                    name: item.Base.Name,
                    sequence: itemIdx
                });
                const id = `${item.Base.Id}`;
                if (!self.categoryContents.has(id)) self.categoryContents.set(id, eventListWithDetailModel.create(<SnapshotOut<typeof eventListWithDetailModel>>{}, env));
                const content = self.categoryContents.get(id);
                content.events = observable(
                    item.Base.Events.map((e, eventIdx) =>
                        EventDataStore.create(<SnapshotOut<typeof EventDataStore>>
                            {
                                Id: e.Id,
                                Name: e.Name,
                                Date: protoUtils.protoTimeStamp2Date(e.Date).getTime(),
                                GraphicsPresentationID: e.GraphicsPresentationId,
                                DetailsCount: e.DetailCount,
                                CategoryPath: e.CategoryPath,
                                Statistics: e.StatisticsExternalId?.length ? StatisticsInApiStore.create(<SnapshotOut<typeof StatisticsInApiStore>>{ id: e.StatisticsExternalId, type: StatisticsType.BRMatch }) : null,
                                GameGroups: content.createGameGroups(e.Id, e.Name, e.GameGroups, withDefaults && eventIdx === 0 && e.Id),
                                ShowDayOfDate: true,
                                EventTvs: e.Tvs && e.Tvs.length
                                    ? observable(
                                        e.Tvs.map(e => {
                                            return { id: e.Id, name: e.Name, date: protoUtils.protoTimeStamp2Date(e.Date).getTime() };
                                        })
                                    )
                                    : undefined,
                                CategoryId: undefined,
                                Info: e.Info,
                                MarketingMessage: e.MarketingMessage,
                                remainingSeconds: null,
                                SuperOddsPopularityStats: observable(e.SuperOddsPopularityStats.map(x => OddsPopularityStatsInApiStore.create(<SnapshotOut<typeof OddsPopularityStatsInApiStore>>{ tipId: x.TipID, name: x.Name, percentage: x.Percentage })))
                            },
                            env
                        )
                    )
                );
            });
            self.categories = observable(categories);
        };

        return {
            loadAsync,
            reloadAsync,
            countDown: () => {
                self.categoryContents.forEach(c => {
                    c.countDown();
                });
            }
        };
    });
